import { useEffect } from 'react';
import { player } from '@/lib/player.ts';
import { usePlayer } from '@/stores/playback.ts';
import { useRecents } from '@/stores/recents.ts';
import { useUi } from '@/stores/ui.ts';
import { Focusable } from '@/ui/Focusable.tsx';
import { ChannelLogo } from '@/ui/ChannelLogo.tsx';

/**
 * "What was I just watching?" — UP over video.
 *
 * A row, not a grid, and only what you have actually watched, because the
 * whole value is that it is SHORT: two presses to get back to the last
 * channel, versus walking a hundred-tile grid. The channel playing right now
 * is left out, so the first tile is always the one before this one.
 */
export function RecentStrip() {
  const { channels } = useRecents();
  const { channel: current } = usePlayer();
  const { recentsOpen, setRecentsOpen } = useUi();

  const list = channels.filter((c) => c.id !== current?.id);

  // Nothing to show is not an empty panel — it is no panel.
  useEffect(() => {
    if (recentsOpen && list.length === 0) setRecentsOpen(false);
  }, [recentsOpen, list.length, setRecentsOpen]);

  const firstId = list[0]?.id;

  // Opening the strip has to LAND somewhere, or the next press of the remote
  // has no origin and goes to whatever is first in the document.
  useEffect(() => {
    if (!recentsOpen || !firstId) return;
    document.querySelector<HTMLElement>(`[data-nav="recent-${firstId}"]`)?.focus();
  }, [recentsOpen, firstId]);

  if (!recentsOpen || list.length === 0) return null;

  return (
    <div className="recents">
      <div className="recents-label">Recently watched</div>
      <div className="recents-row">
        {list.map((c) => (
          <Focusable
            key={c.id}
            id={`recent-${c.id}`}
            className="recent"
            onSelect={() => {
              setRecentsOpen(false);
              void player.play(c).catch(() => { /* surfaced by the player */ });
            }}
          >
            <div className="recent-logo"><ChannelLogo channel={c} className="logo-inner" /></div>
            <div className="recent-name">{c.name}</div>
          </Focusable>
        ))}
      </div>
    </div>
  );
}
